/**
 * CBS Cost Grid — hierarchical AG Grid with chevron expand/collapse and
 * parent rollup, mirroring the CBS Matrix page so the tree stays consistent
 * across modules.
 */
import { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, RefreshCw, Download, Upload, FileSpreadsheet, Loader2, Unlink,
  ChevronDown, ChevronRight, Link2
} from 'lucide-react';

function fmtDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return '—'; }
}

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)} K`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function StatusCellRenderer({ value }) {
  const colors = {
    UNDER_BUDGET: { bg: '#22C55E18', text: '#22C55E', label: 'Under Budget' },
    ON_BUDGET: { bg: '#F59E0B18', text: '#F59E0B', label: 'On Budget' },
    OVER_BUDGET: { bg: '#EF444418', text: '#EF4444', label: 'Over Budget' },
    NO_DATA: { bg: '#6B728018', text: '#6B7280', label: 'No Data' },
  };
  const c = colors[value] || colors.NO_DATA;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: c.bg, color: c.text }}>
      {c.label}
    </span>
  );
}

function CpiCellRenderer({ value }) {
  if (!value) return <span className="text-muted-foreground">--</span>;
  const color = value >= 1.05 ? '#22C55E' : value >= 0.95 ? '#F59E0B' : '#EF4444';
  return <span style={{ color, fontWeight: 600 }}>{value.toFixed(2)}</span>;
}

function ObjectCountRenderer({ value, data }) {
  const count = data?.modelObjects?.length || 0;
  return (
    <div className="flex items-center gap-1">
      <span className="font-mono text-xs">{count}</span>
      {count > 0 && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
    </div>
  );
}

// Natural (numeric-aware) sort key so 1.10 comes after 1.9.
function _sortKey(code) {
  const parts = [];
  for (const seg of String(code || '').split('.')) {
    const n = Number(seg);
    parts.push(Number.isFinite(n) ? [0, n] : [1, seg]);
  }
  return parts;
}
function _cmp(a, b) {
  const A = _sortKey(a), B = _sortKey(b);
  for (let i = 0; i < Math.max(A.length, B.length); i++) {
    const x = A[i] ?? [0, -Infinity];
    const y = B[i] ?? [0, -Infinity];
    if (x[0] !== y[0]) return x[0] - y[0];
    if (x[1] !== y[1]) return x[1] < y[1] ? -1 : 1;
  }
  return 0;
}

export function CostGrid({
  mappings, gridSearchText, onGridSearchChange, onRowClicked,
  onSyncCbs, syncing, onMappingTemplate, onMappingExport, onMappingImport,
  importingMapping, onUnlinkAll, onSync3dFrom4d, selectedCostCode
}) {
  const gridRef = useRef(null);
  const importRef = useRef(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = useCallback((code) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  // Build tree, roll up child totals into parents, and derive the visible flat list.
  const { treeRows, hasChildrenMap } = useMemo(() => {
    if (!mappings || !mappings.length) return { treeRows: [], hasChildrenMap: {} };

    // Self-heal level + parentCostCode from the dotted cost code so pre-fix
    // mappings still render as a tree.
    const byCode = {};
    mappings.forEach(m => {
      const code = String(m.costCode || '');
      const derivedParent = code.includes('.') ? code.split('.').slice(0, -1).join('.') : null;
      const derivedLevel = code.includes('.') ? code.split('.').length - 1 : 0;
      byCode[code] = {
        ...m,
        level: (m.level ?? undefined) === undefined || m.level === 0 ? derivedLevel : m.level,
        parentCostCode: m.parentCostCode || m.parentCode || derivedParent,
        _children: [],
      };
    });
    const codesSet = new Set(Object.keys(byCode));
    const roots = [];
    Object.values(byCode).forEach(n => {
      const p = n.parentCostCode && codesSet.has(n.parentCostCode) ? byCode[n.parentCostCode] : null;
      if (p) p._children.push(n);
      else roots.push(n);
    });
    // Sort each level naturally
    const sortNode = (n) => { n._children.sort((a, b) => _cmp(a.costCode, b.costCode)); n._children.forEach(sortNode); };
    roots.sort((a, b) => _cmp(a.costCode, b.costCode));
    roots.forEach(sortNode);

    // Roll up leaves → parents (always: parents are computed).
    const rollup = (n) => {
      n._children.forEach(rollup);
      if (!n._children.length) return;
      n.budget = n._children.reduce((s, c) => s + (c.budget || 0), 0);
      n.actual = n._children.reduce((s, c) => s + (c.actual || 0), 0);
      n.committed = n._children.reduce((s, c) => s + (c.committed || 0), 0);
      n.variance = n.budget - n.actual;
      n.cpi = n.actual > 0 ? Math.round((n.budget / n.actual) * 100) / 100 : 0;
      const status = n.budget > 0
        ? (n.actual / n.budget <= 0.85 ? 'UNDER_BUDGET' : n.actual / n.budget <= 1.05 ? 'ON_BUDGET' : 'OVER_BUDGET')
        : 'NO_DATA';
      n.status = status;
      // Parent rows should always show the sum of descendant leaf-linked
      // objects so users can see how much of the model is tied to each summary
      // WBS. Overrides any object that got copied onto a summary directly
      // (e.g. from 4D sync).
      const kidObjs = n._children.reduce((s, c) => s + ((c.modelObjects || []).length), 0);
      n.modelObjects = Array.from({ length: kidObjs });
    };
    roots.forEach(rollup);

    // Flatten depth-first, filter by expansion.
    const flat = [];
    const walk = (n) => { flat.push(n); n._children.forEach(walk); };
    roots.forEach(walk);
    const hasKids = {};
    flat.forEach(n => { if (n._children.length) hasKids[n.costCode] = true; });
    const visible = flat.filter(n => {
      let p = n.parentCostCode;
      while (p) {
        if (!expanded.has(p)) return false;
        p = byCode[p]?.parentCostCode;
      }
      return true;
    }).map(n => ({
      ...n,
      _hasChildren: !!hasKids[n.costCode],
      _expanded: expanded.has(n.costCode),
    }));
    return { treeRows: visible, hasChildrenMap: hasKids };
  }, [mappings, expanded]);

  // Seed the expansion set to level-0 whenever the mapping list changes.
  useEffect(() => {
    if (!mappings || !mappings.length) return;
    setExpanded(prev => {
      if (prev.size > 0) return prev;
      const roots = new Set();
      mappings.forEach(m => {
        const code = String(m.costCode || '');
        if (!code.includes('.')) roots.add(code);
      });
      return roots;
    });
  }, [mappings]);

  const CostCodeCell = useCallback((p) => {
    const d = p.data || {};
    const lvl = d.level || 0;
    const hasKids = !!d._hasChildren;
    const isOpen = !!d._expanded;
    return (
      <div className="flex items-center h-full" style={{ paddingLeft: `${lvl * 14}px`, gap: 4 }}>
        {hasKids ? (
          <button
            type="button"
            data-cbs-chevron="1"
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent?.stopImmediatePropagation?.();
              toggleExpand(d.costCode);
            }}
            className="p-0.5 rounded hover:bg-white/10 transition-colors flex items-center justify-center"
            data-testid={`5d-cbs-toggle-${d.costCode}`}
            style={{ width: 14, height: 14 }}
          >
            {isOpen
              ? <ChevronDown className="w-3 h-3 text-slate-400" />
              : <ChevronRight className="w-3 h-3 text-slate-400" />}
          </button>
        ) : (
          <span style={{ width: 14, display: 'inline-block' }} />
        )}
        <span style={{ fontWeight: hasKids ? 600 : 400, fontFamily: 'monospace', fontSize: '11px' }}>{d.costCode}</span>
      </div>
    );
  }, [toggleExpand]);

  const columnDefs = useMemo(() => [
    { field: 'costCode', headerName: 'Cost Code', width: 200, pinned: 'left', cellRenderer: CostCodeCell, sortable: false },
    { field: 'costCodeName', headerName: 'Description', minWidth: 220, flex: 1,
      cellStyle: p => ({ fontWeight: p.data?._hasChildren ? 600 : 400 }) },
    { field: 'level', headerName: 'Lvl', width: 60, cellStyle: { textAlign: 'center' } },
    { field: 'budget', headerName: 'Budget', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { field: 'actual', headerName: 'Actual', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { field: 'committed', headerName: 'Committed', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { field: 'variance', headerName: 'Variance', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value),
      cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', fontWeight: 600 }) },
    { field: 'cpi', headerName: 'CPI', width: 80, cellRenderer: CpiCellRenderer },
    { field: 'status', headerName: 'Status', width: 130, cellRenderer: StatusCellRenderer },
    { field: 'modelObjects', headerName: 'Objects', width: 90, cellRenderer: ObjectCountRenderer },
    { headerName: 'Baseline Start',  field: 'baselineStart',  width: 110, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#64748B', fontSize: '10px' } },
    { headerName: 'Baseline Finish', field: 'baselineFinish', width: 110, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#64748B', fontSize: '10px' } },
    { headerName: 'Forecast Start',  field: 'forecastStart',  width: 110, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#0EA5E9', fontSize: '10px' } },
    { headerName: 'Forecast Finish', field: 'forecastFinish', width: 110, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#0EA5E9', fontSize: '10px' } },
    { headerName: 'Actual Start',    field: 'actualStart',    width: 110, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#22C55E', fontSize: '10px' } },
    { headerName: 'Actual Finish',   field: 'actualFinish',   width: 110, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#22C55E', fontSize: '10px' } },
  ], [CostCodeCell]);

  const defaultColDef = useMemo(() => ({
    sortable: true, filter: true, resizable: true,
    cellStyle: { fontSize: '11px', lineHeight: '28px' },
    headerClass: 'text-[10px]',
  }), []);

  const onFilterChanged = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('quickFilterText', gridSearchText);
    }
  }, [gridSearchText]);

  const handleRowClicked = useCallback((p) => {
    // Chevron clicks are handled inside CostCodeCell — don't propagate to row.
    const target = p.event?.target;
    if (target && target.closest && target.closest('[data-cbs-chevron="1"]')) return;
    // Summary rows both fold and drive the viewer: a user clicking "Civil"
    // expects to see all of Civil in the model, not just a fold.
    if (p.data?._hasChildren) toggleExpand(p.data.costCode);
    onRowClicked(p.data.costCode);
  }, [onRowClicked, toggleExpand]);

  // Keep the row chosen from the 3D model (viewer -> grid) on screen.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !selectedCostCode) return;
    const node = api.getRowNode(selectedCostCode);
    if (node?.rowIndex != null) api.ensureIndexVisible(node.rowIndex, 'middle');
    api.redrawRows();
  }, [selectedCostCode, treeRows]);

  const getRowClass = useCallback(
    (p) => (p.data?.costCode === selectedCostCode ? 'cbs-row-active' : ''),
    [selectedCostCode]
  );

  const expandAll = useCallback(() => {
    setExpanded(new Set(Object.keys(hasChildrenMap)));
  }, [hasChildrenMap]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  return (
    <div className="flex flex-col h-full" data-testid="cost-grid">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b shrink-0 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search cost codes..."
            value={gridSearchText}
            onChange={e => { onGridSearchChange(e.target.value); onFilterChanged(); }}
            className="h-7 pl-8 text-xs"
            data-testid="cost-grid-search"
          />
        </div>
        <Badge variant="outline" className="text-[10px] h-6">{mappings.length} cost codes</Badge>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={expandAll} data-testid="5d-expand-all-btn" title="Expand all">
            <ChevronDown className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={collapseAll} data-testid="5d-collapse-all-btn" title="Collapse all">
            <ChevronRight className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={onSyncCbs} disabled={syncing} data-testid="sync-cbs-btn">
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Sync CBS
          </Button>
          {onSync3dFrom4d && (
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={onSync3dFrom4d} disabled={syncing} data-testid="sync-3d-from-4d-btn" title="Copy 3D-object links from every 4D activity into the matching CBS row">
              <Link2 className="w-3 h-3" /> Sync 3D from 4D
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={onMappingExport} data-testid="export-mappings-btn">
            <Download className="w-3 h-3" /> Export
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={onMappingTemplate} data-testid="template-btn">
            <FileSpreadsheet className="w-3 h-3" /> Template
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => importRef.current?.click()} disabled={importingMapping} data-testid="import-mappings-btn">
            {importingMapping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Import
          </Button>
          <input ref={importRef} type="file" accept=".xlsx" className="hidden" onChange={e => { if (e.target.files?.[0]) onMappingImport(e.target.files[0]); e.target.value = ''; }} />
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-red-400 hover:text-red-300" onClick={onUnlinkAll} data-testid="unlink-all-btn">
            <Unlink className="w-3 h-3" /> Unlink All
          </Button>
        </div>
      </div>
      <div className="flex-1 ag-theme-alpine-dark" data-testid="cost-grid-table">
        <style>{`
          .cbs-row-active .ag-cell {
            background: rgba(14, 165, 233, 0.16) !important;
            color: var(--text-primary) !important;
          }
          .cbs-row-active .ag-cell:first-child { box-shadow: inset 2px 0 0 0 #0EA5E9; }
          [data-testid="cost-grid-table"] .ag-row { cursor: pointer; }
        `}</style>
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          rowData={treeRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          rowHeight={28}
          headerHeight={30}
          getRowId={p => p.data.costCode}
          getRowClass={getRowClass}
          onRowClicked={handleRowClicked}
          quickFilterText={gridSearchText}
          suppressCellFocus
        />
      </div>
    </div>
  );
}
